import {
  ButtonAlignment,
  type DynamicObjectInterface,
  FAIcon,
  IconStyle,
  Modal,
  Slider,
  TextAlign,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import AvatarEditor from 'react-avatar-editor';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Logger } from '@nextgen-web-framework/core';
import { CANVAS_DEFAULT_SIZE, EDITOR_ASPECT_RATIO, COMPELTE_ANGLE, DEFAULT_ROTATION } from '../../utils/Constants';
import { ImageEditorProps, ImageProps } from './types/propTypes';
import styles from './ImageEditor.module.scss';

const ImageEditor = ({
  imageFile,
  onImageEditorClose,
  onConfirmImageClick,
  editorAspectRatio = EDITOR_ASPECT_RATIO.CID,
}: ImageEditorProps) => {
  const editor = useRef<AvatarEditor>(null);
  const t = useTranslation();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const logger = Logger.getInstance();
  const [imageState, setImageState] = useState<ImageProps>({
    image: imageFile,
    ...CANVAS_DEFAULT_SIZE,
  });

  useEffect(() => {
    if (modalRef.current) {
      setImageState({
        ...imageState,
        // height and width of canvas
        height: (modalRef?.current?.offsetWidth - 30) / editorAspectRatio,
        width: modalRef?.current?.offsetWidth - 30,
      });
    }
  }, []);

  const handleScale = (e: ChangeEvent<HTMLInputElement>) => {
    const scale = parseFloat(e.target.value);
    setImageState({ ...imageState, scale });
  };

  const rotateScale = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const sliderRotationActualValue = Number(imageState?.slideRotation) - DEFAULT_ROTATION;
    const value = Math.min(180, Math.max(-180, Number(e.target.value) - DEFAULT_ROTATION));
    setImageState({
      ...imageState,
      rotate: sliderRotationActualValue + parseFloat(value.toString()),
      slideRotation: parseFloat(value.toString()) + DEFAULT_ROTATION,
    });
  };

  const rotateLeft = () => {
    setImageState({
      ...imageState,
      rotate: (Number(imageState?.rotate) - DEFAULT_ROTATION) % COMPELTE_ANGLE,
    });
  };

  const rotateRight = () => {
    setImageState({
      ...imageState,
      rotate: (Number(imageState?.rotate) + DEFAULT_ROTATION) % COMPELTE_ANGLE,
    });
  };

  const logCallback = (e: DynamicObjectInterface) => {
    logger.log('callback', e);
  };

  const saveImage = () => {
    const editorCanvas = editor?.current?.getImage();
    if (!editorCanvas) {
      return;
    }
    const whiteBackgroundCanvas = document.createElement('canvas');
    whiteBackgroundCanvas.width = editorCanvas.width;
    whiteBackgroundCanvas.height = editorCanvas.height;
    const ctx = whiteBackgroundCanvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, whiteBackgroundCanvas.width, whiteBackgroundCanvas.height);
    ctx.drawImage(editorCanvas, 0, 0);
    const dataUrl = whiteBackgroundCanvas.toDataURL('image/jpeg', 0.9);
    onConfirmImageClick(dataUrl); // image best quality
  };

  const imageEditorBody = (
    <div>
      <span className={styles['img-editor-subtitle']}>{t('profile.imageEditor.subheading')}</span>
      <div>
        <div className={styles['img-editor-canvas-container']}>
          <AvatarEditor
            ref={editor}
            scale={imageState?.scale}
            width={imageState?.width}
            height={imageState?.height}
            color={[0, 0, 0, 0.5]} // RGBA sliding window color
            rotate={imageState?.rotate}
            onLoadFailure={logCallback.bind('onLoadFailed')}
            onLoadSuccess={logCallback.bind('onLoadSuccess')}
            onImageReady={logCallback.bind('onImageReady')}
            image={imageState.image}
            style={{
              width: Number(imageState?.width) > Number(modalRef?.current?.offsetWidth) ? '100%' : 'none', // /* Make canvas width responsive */,
              height: 'auto' /* Maintain aspect ratio when width changes */,
              maxWidth: '100% ' /* Limit width to the container's width */,
              backgroundColor: '#707070',
              border: '1px solid #0000',
              aspectRatio: editorAspectRatio, //sliding window aspect ratio
            }}
          />
        </div>
      </div>

      <div className={styles['img-editor-controls']}>
        <div className={styles['img-editor-controls-panel']}>
          <div className={styles['img-editor-rotate-section']}>
            <div
              className={`${styles['img-editor-btn']} {$styles['img-editor-zoom']}`}
              onClick={rotateLeft}
              role="button"
              tabIndex={0}
              aria-label="rotate-left"
              onKeyDown={rotateLeft}
              data-testid="rotate-left-id"
            >
              <FAIcon
                iconName="rotate-left"
                theme={Theme.PURPLE}
                iconStyle={IconStyle.SOLID}
                className={styles['img-editor-rotate-btn']}
              />
              <span className={styles['img-editor-btn']}>{t('profile.imageEditor.rotateLeftBtn')}</span>
            </div>
            <div
              className={styles['img-editor-btn']}
              onClick={rotateRight}
              role="button"
              tabIndex={0}
              aria-label="rotate-right"
              onKeyDown={rotateRight}
              data-testid="rotate-right-id"
            >
              <FAIcon
                iconName="rotate-right"
                theme={Theme.PURPLE}
                iconStyle={IconStyle.SOLID}
                className={styles['img-editor-rotate-btn']}
              />
              <span className={styles['img-editor-btn']}>{t('profile.imageEditor.rotateRightBtn')}</span>
            </div>
          </div>

          <div className={styles['img-editor-zoom']} role="button" tabIndex={0} aria-label="Rotation">
            <span>{t('profile.imageEditor.rotate')}</span>
            <Slider
              onChange={rotateScale}
              min="0"
              max="180"
              step="1"
              value={imageState.slideRotation}
              data-testid="rotate-testid"
            />
          </div>

          <div className={styles['img-editor-zoom']} role="button" tabIndex={0} aria-label="Zoom">
            <span>{t('profile.imageEditor.zoom')}</span>
            <Slider
              min="0.1"
              max="2"
              step="0.01"
              value={imageState.scale}
              defaultValue={1}
              onChange={handleScale}
              data-testid="zoom-testid"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        onClose={onImageEditorClose}
        headline={t('profile.imageEditor.heading')}
        primaryButtonLabel={t('profile.imageEditor.confirm')}
        secondaryButtonLabel={t('profile.imageEditor.cancel')}
        onSecondaryButtonClick={onImageEditorClose}
        onPrimaryButtonClick={saveImage}
        textAlign={TextAlign.CENTER}
        closeOnBackdropClick={false}
        buttonAlignment={ButtonAlignment.HORIZONTAL}
        className={styles['img-modal']}
        ref={modalRef}
      >
        {imageEditorBody}
      </Modal>
    </>
  );
};
export default ImageEditor;
